import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { formatCode } from '../api';
import { Button, Card, Link, Loading, Pill, Tabs } from '../ui';
import { GameSocket, type StoryPassage } from './socket';

/**
 * Story play: a game started from a compiled story. Players pick one of the story's main characters
 * in the lobby, the composer starts the game, and each player then types what their character does;
 * the server's GM and area agents answer from that character's point of view.
 */
export function StoryGame(p: { code: string; isGm: boolean }) {
	const sock = useMemo(() => new GameSocket(p.code), [p.code]);
	useEffect(() => () => sock.close(), [sock]);
	const game = sock.game.use();
	const you = sock.you.use();
	const connected = sock.connected.use();
	const kicked = sock.kicked.use();
	const [tab, setTab] = useState<'gm' | 'char'>(p.isGm ? 'gm' : 'char');
	if (kicked) return <main class="page narrow"><Card title="Removed"><p>{kicked}</p><Link href="/home">Home</Link></Card></main>;
	if (!game) return <Loading />;
	const playing = game.status !== 'lobby' && you?.characterId;
	return (
		<main class="game">
			<div class="game-bar">
				<div class="game-title">
					<strong>{game.name}</strong> <code class="code">{formatCode(game.code)}</code> <Pill tone={game.status === 'active' ? 'ok' : game.status === 'ended' ? 'muted' : 'accent'}>{game.status}</Pill>
					{!connected ? <Pill tone="warn">reconnecting…</Pill> : null}
				</div>
				{p.isGm && game.status !== 'lobby' ? (
					<Tabs
						value={tab}
						onChange={setTab}
						tabs={[
							{ id: 'gm', label: 'Composer' },
							{ id: 'char', label: 'Character' },
						]}
					/>
				) : null}
			</div>
			{game.status === 'lobby' ? <Lobby sock={sock} isGm={p.isGm} /> : p.isGm && tab === 'gm' ? <Composer sock={sock} /> : playing ? <PlayView sock={sock} /> : <Lobby sock={sock} isGm={p.isGm} />}
		</main>
	);
}

/** Pick a main character; the composer starts the game. Players can also join and pick once it is running. */
function Lobby(p: { sock: GameSocket; isGm: boolean }) {
	const game = p.sock.game.use();
	const you = p.sock.you.use();
	const players = p.sock.players.use();
	const chars: any[] = game?.claimable ?? [];
	const mine = you?.characterId;
	return (
		<div class="page narrow">
			<Card title={game?.title || 'Choose your character'}>
				<p class="small muted">Each player plays one of the story's main characters. Everyone else in the story is moved by the story itself.</p>
				<div class="cast-grid">
					{chars.map((c) => {
						const by = players.find((x: any) => x.characterId === c.id);
						const isMine = mine === c.id;
						return (
							<button key={c.id} class={`cast-card tier-main ${isMine ? 'sel' : ''}`} disabled={!!by && !isMine} onClick={() => p.sock.send({ t: 'claim', character: isMine ? null : c.id })} aria-pressed={isMine}>
								<strong>{c.name}</strong>
								{c.role ? <span class="small muted">{c.role}</span> : null}
								{c.description ? <span class="small">{c.description.slice(0, 110)}</span> : null}
								<span class="small">{isMine ? '✓ You' : by ? `Played by ${by.name}` : 'Open: tap to play'}</span>
							</button>
						);
					})}
				</div>
				<h3>At the table</h3>
				<ul class="list small">
					{players.map((x: any) => (
						<li key={x.userId}>
							{x.name}
							{x.role === 'gm' ? ' (composer)' : ''} · {x.characterName ?? <span class="muted">choosing…</span>} {x.online ? null : <span class="muted">(offline)</span>}
						</li>
					))}
				</ul>
				{game?.status === 'lobby' ? (
					p.isGm ? (
						<Button onClick={() => p.sock.send({ t: 'start' })} disabled={!players.some((x: any) => x.characterId)}>
							Start the story
						</Button>
					) : (
						<p class="small muted">Waiting for the composer to start the story…</p>
					)
				) : (
					<p class="small muted">The story is under way: pick an open character to join in.</p>
				)}
			</Card>
		</div>
	);
}

/** A player's screen: their passages and command box, beside where they are and where the story takes them next. */
function PlayView(p: { sock: GameSocket }) {
	const view = p.sock.storyView.use();
	const feed = p.sock.storyFeed.use();
	const [text, setText] = useState('');
	const end = useRef<HTMLDivElement>(null);
	useEffect(() => end.current?.scrollIntoView({ block: 'end' }), [feed.length, view?.busy]);
	if (!view) return <Loading />;
	const send = () => {
		if (!text.trim()) return;
		p.sock.send({ t: 'command', text });
		setText('');
	};
	return (
		<div class="story-play">
			<section class="story-scene">
				<div class="story-feed" aria-live="polite">
					{feed.length ? feed.map((x, i) => <Line key={i} p={x} />) : <p class="muted small">Your story starts here. Look around, or say what you do.</p>}
					{view.busy ? (
						<p class="muted small">
							<span class="spinner" aria-hidden="true" /> The story unfolds…
						</p>
					) : null}
					<div ref={end} />
				</div>
				<form
					class="story-input"
					onSubmit={(e) => {
						e.preventDefault();
						send();
					}}
				>
					<textarea
						rows={2}
						value={text}
						placeholder={`What does ${view.character.name} do?`}
						aria-label="What your character does"
						onInput={(e) => setText(e.currentTarget.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								send();
							}
						}}
					/>
					<div class="row">
						<Button type="submit" small disabled={!text.trim()}>
							Do it
						</Button>
						<Button type="button" small variant="ghost" onClick={() => p.sock.send({ t: 'look' })}>
							Look around
						</Button>
						<Button type="button" small variant="ghost" onClick={() => p.sock.send({ t: 'move_on' })} disabled={!view.next}>
							{view.next?.here ? 'What now?' : 'Move on'}
						</Button>
					</div>
				</form>
			</section>
			<aside class="story-side">
				<Card title={view.character.name}>
					{view.character.role ? <p class="small muted">{view.character.role}</p> : null}
					<h3>Where</h3>
					<p>{view.location ? view.location.path : <span class="muted">Not in the story yet</span>}</p>
					<h3>Here</h3>
					{view.present.length ? (
						<ul class="list small">
							{view.present.map((x: any) => (
								<li key={x.id}>
									{x.name} {x.role ? <span class="muted">· {x.role}</span> : null} {x.played ? <Pill tone="accent">player</Pill> : null}
								</li>
							))}
						</ul>
					) : (
						<p class="small muted">Nobody else.</p>
					)}
					<h3>Go to</h3>
					{view.destinations?.length ? (
						<div class="go-list">
							{view.destinations.map((d: any) => (
								<Button key={d.id} type="button" small variant={d.story ? undefined : 'ghost'} disabled={view.busy} onClick={() => p.sock.send({ t: 'go', to: d.id })} title={d.story ? 'Where the story leads next' : undefined}>
									{d.story ? `★ ${d.name}` : d.name}
								</Button>
							))}
						</div>
					) : (
						<p class="small muted">Nowhere else nearby that you know of. You can also type where you go.</p>
					)}
					<h3>You carry</h3>
					{view.inventory.length ? (
						<ul class="list small">
							{view.inventory.map((i: any) => (
								<li key={i.id}>
									{i.name}
									{i.quantity > 1 ? <span class="muted"> × {i.quantity}</span> : null}
								</li>
							))}
						</ul>
					) : (
						<p class="small muted">Nothing.</p>
					)}
					<h3>Next</h3>
					{view.next ? (
						<>
							<p>
								<strong>{view.next.to}</strong>
								{view.next.here ? <span class="small muted"> · you're here; the story moves on once:</span> : null}
							</p>
							{view.next.conditions.length ? (
								<ul class="checklist small">
									{view.next.conditions.map((x: any) => (
										<li key={x.id} class={x.met ? 'met' : ''}>
											{x.met ? '✓' : '○'} {x.description}
										</li>
									))}
								</ul>
							) : (
								<p class="small muted">Nothing holds you here.</p>
							)}
						</>
					) : (
						<p class="small muted">The story has nowhere further for you to go for now.</p>
					)}
				</Card>
			</aside>
		</div>
	);
}

function Line(p: { p: StoryPassage }) {
	return <p class={`story-line story-${p.p.kind}`}>{p.p.kind === 'you' ? `› ${p.p.text}` : p.p.text}</p>;
}

/** The composer's overview: every place someone is, what happened there lately, and how far each character has got. */
function Composer(p: { sock: GameSocket }) {
	const gm = p.sock.storyGm.use();
	if (!gm) return <Loading />;
	return (
		<div class="page wide">
			<div class="two-col wide-left">
				<div>
					{gm.areas.map((a: any) => (
						<Card key={a.id} title={a.name} actions={a.busy ? <Pill tone="accent">working…</Pill> : null}>
							<p class="small">
								{a.present.map((x: any, i: number) => (
									<span key={x.id}>
										{i ? ', ' : ''}
										{x.played ? <strong>{x.name}</strong> : x.name}
									</span>
								))}
							</p>
							{a.log.length ? (
								<ul class="list small">
									{a.log.map((x: string, i: number) => (
										<li key={i}>{x}</li>
									))}
								</ul>
							) : (
								<p class="small muted">Nothing has happened here yet.</p>
							)}
						</Card>
					))}
				</div>
				<Card title="Characters">
					<ul class="list small">
						{gm.characters.map((x: any) => (
							<li key={x.id}>
								<strong>{x.name}</strong> {x.player ? <Pill tone="accent">{x.player}</Pill> : <span class="muted">(story)</span>}
								<br />
								{x.location ?? <span class="muted">offstage</span>} · {Math.min(x.done, x.steps)}/{x.steps} steps
								{x.next ? (
									<>
										<br />
										<span class="muted">
											next: {x.next.to}
											{x.next.unmet.length ? ` once ${x.next.unmet.join('; ')}` : ''}
										</span>
									</>
								) : null}
							</li>
						))}
					</ul>
				</Card>
			</div>
		</div>
	);
}
